import { selectIsFeatureFlagVariation } from '@noths/polaris-client-user-configuration';
import { createAsyncThunk } from '@reduxjs/toolkit';
import getConfig from 'next/config';
import * as R from 'ramda';

import { navigateToCart } from 'src/components/organisms/ProductInfo/modules/thunks';
import { DisplayCrossSellModal } from 'src/constants/featureFlags';
import { trackLoop54Event } from 'src/redux/loop54';
import {
  AddToBasketResponse,
  GetBasketWithOptionsResponse,
  NextConfig,
  ProductOptionInput,
  ProductOptionValue,
  RemoveItemsFromBasketResponse,
  UpdateItemBasketResponse,
} from 'src/types';
import { ReduxApplicationState } from '../combinedReducer';
import { SelectedPersonalisationsRecord } from '../personalisation';
import * as queries from './graphql-queries';

const { publicRuntimeConfig } = getConfig() as NextConfig;
const BASKET_GRAPHQL_ENDPOINT = publicRuntimeConfig.endpoints.basket;

type ProductOptionInputWithoutLabel = Pick<ProductOptionInput, 'optionId' | 'value' | 'valueId'>[];

export interface AddItemToBasketQueryVariables {
  id?: string;
  code?: string;
  quantity: number;
  options: ProductOptionInputWithoutLabel;
}

export interface AddCrossSellProductToBasketQueryVariables {
  code: string;
  quantity: number;
}

export interface RemoveItemFromBasket {
  itemIds: number[];
}

export type QueryVariables =
  | AddItemToBasketQueryVariables
  | AddCrossSellProductToBasketQueryVariables
  | RemoveItemFromBasket;

export interface QueryBody {
  body: {
    query: string;
    variables?: QueryVariables;
  };
}

const basketAPI = {
  endpoint: BASKET_GRAPHQL_ENDPOINT,
  name: 'basket-api',
};

const dispatchBasketEvent = () => {
  const event = new Event('basketUpdated');
  document.dispatchEvent(event);
};

const graphqlRequest = async <T>(query: string, variables?: QueryVariables) => {
  window.Amplify.Configure.API.REST.addEndpoint(basketAPI);

  return await window.Amplify.default.API.post<QueryBody, T>(basketAPI.name, '/graphql', {
    body: {
      query,
      variables,
    },
  });
};

const mapProductOptionsToBasketOptions = (
  selectedPersonalisations: SelectedPersonalisationsRecord,
): ProductOptionInputWithoutLabel =>
  R.chain(
    (optionId) =>
      /* istanbul ignore next */
      selectedPersonalisations?.[optionId]?.map(({ id, name, isCustomInput }: ProductOptionValue) =>
        isCustomInput ? { optionId, value: name } : { optionId, valueId: String(id) },
      ) ?? [],
    Object.keys(selectedPersonalisations),
  );

export const addCrossSellProductToBasket = createAsyncThunk<number, number>(
  'basket/addCrossSellProductToBasket',
  async (code, { getState, dispatch }) => {
    const state = getState() as ReduxApplicationState;

    const {
      userConfiguration: { currencyCode },
    } = getState() as ReduxApplicationState;
    const apiResponse = await graphqlRequest<AddToBasketResponse>(
      queries.addItemToBasketMutation(currencyCode ?? 'GBP'),
      {
        code: String(code),
        quantity: 1,
      },
    );

    if (apiResponse.data.addItem.errors.length) {
      const { message } = apiResponse.data.addItem.errors[0];
      return Promise.reject(message);
    }

    dispatchBasketEvent();

    const isCrossSellDirectToCart = selectIsFeatureFlagVariation(
      state,
      DisplayCrossSellModal.NAME,
      DisplayCrossSellModal.DIRECT_TO_CART_ON_ADD_TO_BASKET,
    );

    if (isCrossSellDirectToCart) {
      dispatch(navigateToCart());
    }

    return code;
  },
);
export const removeCrossSellProductFromBasket = createAsyncThunk<number, number>(
  'basket/removeCrossSellProductFromBasket',
  async (code, { dispatch }) => {
    const res = await dispatch(removeItemFromBasket(code)).unwrap();

    return res;
  },
);

export const removeItemFromBasket = createAsyncThunk<number, number>(
  'basket/removeItemFromBasket',
  async (code) => {
    const apiResponse = await graphqlRequest<RemoveItemsFromBasketResponse>(
      queries.removeItemsMutation(),
      {
        itemIds: [code],
      },
    );

    if (apiResponse.data.removeItems.errors?.length) {
      const { message } = apiResponse.data.removeItems.errors[0];
      return Promise.reject(message);
    }

    dispatchBasketEvent();

    return code;
  },
);

export const addItemToBasket = createAsyncThunk<AddToBasketResponse>(
  'basket/addItemToBasket',
  async (_, { getState, dispatch }) => {
    const {
      product: { code, quantity },
      personalisation: { selectedPersonalisations },
      userConfiguration: { currencyCode },
    } = getState() as ReduxApplicationState;
    const options = mapProductOptionsToBasketOptions(selectedPersonalisations);

    const apiResponse = await graphqlRequest<AddToBasketResponse>(
      queries.addItemToBasketMutation(currencyCode ?? 'GBP'),
      {
        code: String(code),
        quantity,
        options,
      },
    );

    if (apiResponse.data.addItem.errors.length) {
      const { message } = apiResponse.data.addItem.errors[0];
      return Promise.reject(message);
    }

    dispatchBasketEvent();

    dispatch(trackLoop54Event({ eventType: 'addtocart' }));

    return apiResponse;
  },
);

export const updateItem = createAsyncThunk<UpdateItemBasketResponse>(
  'basket/updateItem',
  async (_, { getState }) => {
    const {
      product: { quantity },
      personalisation: { editedItem, selectedPersonalisations },
      userConfiguration: { currencyCode },
    } = getState() as ReduxApplicationState;

    const options = mapProductOptionsToBasketOptions(selectedPersonalisations);

    const apiResponse = await graphqlRequest<UpdateItemBasketResponse>(
      queries.updateItemMutation(currencyCode ?? 'GBP'),
      {
        id: editedItem as string,
        quantity,
        options,
      },
    );

    if (apiResponse.data.updateItem.errors.length) {
      const { message } = apiResponse.data.updateItem.errors[0];
      return Promise.reject(message);
    }

    dispatchBasketEvent();

    return apiResponse;
  },
);

export const getBasketWithOptions = createAsyncThunk<GetBasketWithOptionsResponse>(
  'basket/getBasketWithOptions',
  async () =>
    await graphqlRequest<GetBasketWithOptionsResponse>(
      queries.getProductCustomisationOptionsQuery(),
    ),
);
